import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, Stack, Chip, Checkbox, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, Button,
  ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import EditIcon   from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNotify } from 'react-admin';
import authFetch, { responseError } from '../utils/authFetch';
import { NAVY, tbl, typo } from '../styles/ds';
import TaskDialog from './TaskDialog';
import { useFitCount } from '../hooks/useFitCount';
import FitPagination from '../components/FitPagination';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const s = String(iso).split('T')[0];
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return '—';
  return new Date(y, m - 1, d).toLocaleDateString('he-IL');
};

const PRIORITY_LABEL = { high: 'גבוהה', medium: 'בינונית', low: 'נמוכה' };
const PRIORITY_COLOR = {
  high:   { bg: '#fdecea', color: '#c62828' },
  medium: { bg: '#fff8e1', color: '#e65100' },
  low:    { bg: '#e8f5e9', color: '#2e7d32' },
};

const parseDue = (due) => {
  if (!due) return null;
  const s = String(due).split('T')[0];
  const [y, m, d] = s.split('-').map(Number);
  return (y && m && d) ? new Date(y, m - 1, d) : null;
};

const dueDateStyle = (due) => {
  const dt = parseDue(due);
  if (!dt) return {};
  const diff = Math.floor((dt - new Date().setHours(0,0,0,0)) / 86400000);
  if (diff < 0)  return { color: '#c62828', fontWeight: 700 };
  if (diff === 0) return { color: '#e65100', fontWeight: 700 };
  if (diff === 1) return { color: '#f57c00', fontWeight: 600 };
  return {};
};

const TasksPage = () => {
  const notify = useNotify();

  const [tasks,       setTasks]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [scope,       setScope]       = useState('all');        // 'mine' | 'all'
  const [statusFilter, setStatusFilter] = useState('all');     // 'pending' | 'all'
  const [dialogOpen,  setDialogOpen]  = useState(false);
  const [editing,     setEditing]     = useState(null);         // task being edited
  const [page, setPage] = useState(1);
  const { fitCount, renderCount, containerRef, startIndex } = useFitCount({ fallback: 10, page });

  const load = useCallback(() => {
    setLoading(true);
    const f = {};
    if (scope === 'mine') f.mine = 'true';
    if (statusFilter !== 'all') f.status = statusFilter;
    authFetch(`/tasks?filter=${encodeURIComponent(JSON.stringify(f))}&_start=0&_end=1000`)
      .then(r => r.json())
      .then(d => setTasks(Array.isArray(d) ? d : []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [scope, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [tasks]);

  const pageItems = useMemo(() => {
    return tasks.slice(startIndex, startIndex + renderCount);
  }, [tasks, startIndex, renderCount]);

  const handleToggleDone = async (task) => {
    const newStatus = task.status === 'done' ? 'pending' : 'done';
    await authFetch(`/tasks/${task.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...task, status: newStatus }),
    });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('למחוק את המשימה?')) return;
    const r = await authFetch(`/tasks/${id}`, { method: 'DELETE' });
    if (r.ok) { notify('המשימה נמחקה', { type: 'success' }); load(); }
    else notify(await responseError(r, 'שגיאה במחיקה'), { type: 'error' });
  };

  const handleSaved = () => { setDialogOpen(false); setEditing(null); load(); };

  return (
    <Box className="crm-page" p={3}>
      <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography {...typo.pageTitle}>משימות ותזכורות</Typography>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          sx={{ backgroundColor: NAVY, borderRadius: 2, '&:hover': { backgroundColor: '#162d5a' } }}>
          משימה חדשה
        </Button>
      </Stack>

      {/* Filters */}
      <Stack className="crm-wrap" direction="row" spacing={2} mb={3} flexWrap="wrap">
        <ToggleButtonGroup value={scope} exclusive size="small"
          onChange={(_, v) => v && setScope(v)}
          sx={{ '& .MuiToggleButton-root': { fontSize: 13, px: 2 } }}>
          <ToggleButton value="mine">שלי</ToggleButton>
          <ToggleButton value="all">כולן</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup value={statusFilter} exclusive size="small"
          onChange={(_, v) => v && setStatusFilter(v)}
          sx={{ '& .MuiToggleButton-root': { fontSize: 13, px: 2 } }}>
          <ToggleButton value="pending">פתוחות</ToggleButton>
          <ToggleButton value="all">הכל</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {loading ? (
        <Typography color="text.secondary">טוען...</Typography>
      ) : tasks.length === 0 ? (
        <Typography color="text.secondary">
          {statusFilter === 'pending' ? 'אין משימות פתוחות 🎉' : 'אין משימות'}
        </Typography>
      ) : (
        <Card sx={{ ...tbl.container, border: '1px solid rgba(13,29,62,0.06)' }}>
          <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: NAVY }}>
                <TableCell sx={{ width: 44, color: '#fff' }} />
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>כותרת</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">עדיפות</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">תאריך יעד</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>מוקצה ל</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>קשור ל</TableCell>
                <TableCell sx={{ width: 80, color: '#fff' }} />
              </TableRow>
            </TableHead>
            <TableBody ref={containerRef}>
              {pageItems.map((t, i) => {
                const done = t.status === 'done';
                const pc = PRIORITY_COLOR[t.priority] || PRIORITY_COLOR.medium;
                return (
                  <TableRow key={t.id} sx={{
                    ...tbl.bodyRow(i),
                    opacity: done ? 0.55 : 1,
                  }}>
                    <TableCell>
                      <Checkbox size="small" checked={done} onChange={() => handleToggleDone(t)} />
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={14} fontWeight={600}
                        sx={{ textDecoration: done ? 'line-through' : 'none' }}>
                        {t.title}
                      </Typography>
                      {t.description && (
                        <Typography fontSize={12} color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                          {t.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={PRIORITY_LABEL[t.priority] || t.priority} size="small"
                        sx={{ height: 20, fontSize: 11, fontWeight: 700, backgroundColor: pc.bg, color: pc.color }} />
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 13, ...dueDateStyle(t.due_date) }}>
                      {fmtDate(t.due_date)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {t.assigned_to_username || <span style={{ color: '#aaa' }}>לא מוקצה</span>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {t.entity_type && t.entity_id ? (
                        <Chip size="small"
                          label={`${t.entity_type === 'customer' ? 'לקוח' : t.entity_type === 'order' ? 'הזמנה' : 'ליד'} #${t.entity_id}`}
                          sx={{ height: 20, fontSize: 11 }} />
                      ) : <span style={{ color: '#aaa' }}>כללי</span>}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="ערוך">
                          <IconButton size="small" onClick={() => { setEditing(t); setDialogOpen(true); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="מחק">
                          <IconButton size="small" onClick={() => handleDelete(t.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table></Box>
          <FitPagination page={page} setPage={setPage} total={tasks.length} perPage={fitCount} />
        </Card>
      )}

      <TaskDialog
        open={dialogOpen}
        task={editing}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSaved={handleSaved}
      />
    </Box>
  );
};

export default TasksPage;
